import { type Brand, getBrand } from '@alto/design-system';
import { getCompanyPhone } from './config/getCompanyPhone';
import { getCompanyName } from '~shared/config/getCompanyName';

// Marketing Website
// Brand-keyed so a future brand slots in as one new entry.
const COMPANY_DOMAINS: Record<Brand, string> = {
  // Use hello.fuzehealth.com for 10/1 rebranding launch,
  // switching to fuzehealth.com/pharmacy once fully live, likely 1/1
  fuzerx: 'https://hello.fuzehealth.com',
};

export const getCompanyDomain = (): string => COMPANY_DOMAINS[getBrand()];
const getLegalPath = (): string => `${getCompanyDomain()}/legal`;

export const BIOMETRIC_EXPIRY_TIME = '@user:biometricExpiryTime';
export const APPSTATE_FOREGROUND = 'active';
export const APPSTATE_TRANSITIONING = 'inactive';
export const CAMERA_PERMISSIONS_ERROR = 'Camera permissions not granted';

export const PRESCRIPTION_STATES = {
  ACTIVE_RENEWED: 'active renewed',
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  DISCONTINUED: 'discontinued',
  EXPIRED: 'expired',
  NO_REFILLS: 'no refills',
  RENEWED: 'renewed',
  TOO_EARLY: 'too early',
  TRANSFERRED: 'transferred',
} as const;

export const GENDERS = {
  F: 'Female',
  M: 'Male',
  U: 'Other',
} as const;

export const PRONOUNS: Record<string, string> = {
  she: 'She/Her/Hers',
  he: 'He/Him/His',
  they: 'They/Them/Theirs',
} as const;

export const NO_KNOWN_ALLERGIES = 'No Known Allergies';
export const NO_KNOWN_MEDICAL_CONDITIONS = 'No Known Medical Conditions';
export const HAS_OTHER_SCRIPTS = 'Yes';
export const DOES_NOT_HAVE_OTHER_SCRIPTS = 'No';

export const APP_DOWNLOAD_UPSELL_MODAL = {
  has_replied: 'hasRepliedToGetAppModal',
  has_selected_app: 'app',
  has_selected_browser: 'browser',
} as const;

export const CANCELATION_REASONS = [
  { key: 'too_expensive', label: 'Too expensive', priority: 1 },
  { key: 'discontinued_rx', label: 'Discontinued rx', priority: 3 },
  { key: 'provider_canceled_rx', label: 'Physician changed or canceled rx', priority: 4 },
  { key: 'filled_at_different_pharmacy', label: 'Filled at a different pharmacy', priority: 5 },
  { key: 'unknown_rx_or_provider', label: 'Unknown prescription or provider', priority: 10 },
  { key: 'provider_consult_needed', label: 'Provider consult needed', priority: 11 },
  { key: 'poor_patient_experience', label: 'Poor patient experience', priority: 12 },
  { key: 'delivery_date_too_late', label: 'Delivery date too late', priority: 14 },
  { key: 'other', label: 'Other, please specify', priority: 999 },
] as const;

export const REFILL_REASONS = [
  { key: 'refill_too_soon', label: 'Refill too soon or rx not needed yet', priority: 2 },
  { key: 'side_effects_experienced', label: 'Experienced side effects', priority: 8 },
] as const;

type CancelReasons = (typeof CANCELATION_REASONS)[number];
type RefillReasons = (typeof REFILL_REASONS)[number];
export type CancelationReason = CancelReasons['key'] | RefillReasons['key'] | 'remove_medication';

export const SAFE_PLACE_OPTIONS = [
  {
    value: 'true',
    label: 'Yes, packages may be left',
    descriptions: undefined,
  },
  {
    value: 'false',
    label: 'No, hand packages to recipient',
    descriptions: ['Do not leave packages unattended'],
  },
];

export const SAFE_PLACES = new Set([
  'Front door',
  'Back door',
  'Side porch',
  'Garage',
  'Building reception',
  'Mailroom or property staff',
  'No preference',
]);
export const SAFE_PLACE_INSTRUCTIONS_OPTIONS = Array.from(SAFE_PLACES).map((option) => ({
  value: option,
  label: option,
}));

export const SAFE_PLACE_INSTRUCTIONS_PLACEHOLDER =
  'A safe location where your medication can be placed (e.g. behind the blue planter).';

export const DELIVERY_INSTRUCTIONS_PLACEHOLDER =
  'Please include any additional information for the driver,' +
  ' like gate code or distinct identifiers to help locate your address.';

export const RELATIONSHIP_CODES = {
  SELF: '1',
  SPOUSE: '2',
  CHILD: '3',
  OTHER: '4',
  PARTNER: '8',
  NOT_APPLICABLE: '0',
} as const;

export const RELATIONSHIP_OPTIONS = [
  {
    value: RELATIONSHIP_CODES.SELF,
    label: 'Self',
    personCode: '01',
  },
  {
    value: RELATIONSHIP_CODES.SPOUSE,
    label: 'Spouse',
    personCode: '02',
  },
  {
    value: RELATIONSHIP_CODES.CHILD,
    label: 'Child',
    personCode: '03',
  },
  {
    value: RELATIONSHIP_CODES.OTHER,
    label: 'Other',
    personCode: null,
  },
  {
    value: RELATIONSHIP_CODES.PARTNER,
    label: 'Partner',
    personCode: '02',
  },
  {
    value: RELATIONSHIP_CODES.NOT_APPLICABLE,
    label: 'N/A',
    personCode: null,
  },
];

export const ADDRESS_FORM_FIELDS = {
  id: 'id',
  street_address_1: 'street_address_1',
  street_address_2: 'street_address_2',
  primary: 'primary',
  safe_place: 'safe_place',
  safe_place_instructions: 'safe_place_instructions',
  delivery_instructions: 'delivery_instructions',
  city: 'city',
  state: 'state',
  zip: 'zip',
} as const;

export const FEEDBACK_CATEGORIES = {
  scheduling: 'Scheduling or editing a delivery',
  refill: 'Requesting a refill authorization',
  deliveries: 'Viewing the status of a delivery',
  messaging: 'Sending a message',
  profile: 'Updating my personal information',
  insurance: 'Adding or editing my insurance',
  address: 'Adding or editing an address',
  payment: 'Adding or editing payment methods',
  other: 'Other',
} as const;

export const CONTACT_US_ORIGIN = {
  AutoRefillInfoActionSheet: 'AutoRefillInfoActionSheet',
  ChangeQuantityList: 'ChangeQuantityList',
  CheckoutDateSelector: 'CheckoutDateSelector',
  CheckoutPaymentSelector: 'CheckoutPaymentSelector',
  ConfirmRenewalRequestModal: 'ConfirmRenewalRequestModal',
  ContactCareActionSheet: 'ContactCareActionSheet',
  ContactSupportNeedHelpWithRxCard: 'ContactSupportNeedHelpWithRxCard',
  ContactUsActionSheet: 'ContactUsActionSheet',
  DatePickerActionSheet: 'DatePickerActionSheet',
  DatePickerContainer: 'DatePickerContainer',
  DeliveryDetails: 'DeliveryDetails',
  DeliveryDetailsPickup: 'DeliveryDetails_Pickup',
  DosageChangeContactUsActionSheet: 'DosageChangeContactUsActionSheet',
  ExcessSupplyContactUsActionSheet: 'ExcessSupplyContactUsActionSheet',
  LeftOverMedsContactProgynyActionSheet: 'LeftoverMedsContactProgynyActionSheet',
  FertilityQuantity: 'FertilityQuantity',
  Help: 'Help',
  MaximizeBenefitsActionSheet: 'MaximizeBenefitsActionSheet',
  MedicationDeliveryStatusPanel: 'MedicationDeliveryStatusPanel',
  MessageModal: 'MessageModal',
  MessagingActionSheet: 'MessagingActionSheet',
  MissingNeedlesReminder: 'MissingNeedlesReminder',
  NextAvailableDate: 'NextAvailableDate',
  NotCoveredByInsuranceMessages: 'NotCoveredByInsuranceMessages',
  OnboardingPersonal: 'OnboardingPersonal',
  OrderSuccess: 'OrderSuccess',
  ProfileSupport: 'ProfileSupport',
  PharmacyInfo: 'PharmacyInfo',
  QuantitiesReviewAlert: 'QuantitiesReviewAlert',
  SupportCase: 'SupportCase',
  TransferOutFaq: 'TransferOutFaq',
} as const;

export const CALL_BUTTON_ORIGIN = {
  Help: 'Help',
  IdentityVerification: 'IdentityVerification',
  LoginHelpers: 'LoginHelpers',
  MessageModal: 'MessageModal',
  NotMe: 'NotMe',
  OnboardingGeoVerify: 'OnboardingGeoVerify',
  Profile: 'Profile',
  VerificationBlocked: 'VerificationBlocked',
} as const;

export const EXPENSIVE_THRESHOLD_3000 = 3000;

export const ALTO_PHONE = '1-800-874-5881';
export const FUZERX_PHONE = '1-800-874-5881';
export const PROGYNY_PHONE = '1 (833) 682-8243';
// Keep the main fax number for FuzeRx
export const FAX = '1 (415) 484-7058';

// Updated company-based URLs and email
// TODO: confirm email for FuzxRx
export const getEmail = (): string => 'rx.care@fuzehealth.com';
export const getPrivacyPolicyUrl = (): string => `${getLegalPath()}/privacy`;
//TODO: confirm legal path for FuzeRx
export const getPharmacyNoticesUrl = (): string => `${getLegalPath()}/npp`;
export const getTermsOfServiceUrl = (): string => `${getLegalPath()}/terms-and-conditions`;
export const getProgynyUnboxingGuideUrl = (): string => `${getCompanyDomain()}/fertility-unboxing`;
export const getLocationsUrl = (): string => `${getCompanyDomain()}/locations`;
export const getFaqsUrl = (): string => `${getCompanyDomain()}/faqs`;
export const getInjectionGuidesUrl = (): string => `${getCompanyDomain()}/injection-guides`;
export const getFertilityAffordabilityUrl = (): string => `${getCompanyDomain()}/fertility-affordability`;
export const getHowItWorksUrl = (): string => `${getCompanyDomain()}/how-it-works`;

export const INIT = '__INIT__';

export const BILLING_ISSUES = {
  refill_too_soon: 'billing_issue_refill_too_soon',
  needs_insurance: 'billing_issue_needs_insurance',
  process_insurance_choice: 'billing_issue_process_insurance_choice',
} as const;

export const BILLING_STATUS = {
  REFILL_TOO_SOON: 'waiting_next_billable_date',
  PROGYNY_REFILL_TOO_SOON: 'waiting_progyny_next_billable_date',
};

export const DEFAULT_ERROR_ALERT_TITLE = 'Something went wrong';
export const DEFAULT_ERROR_ALERT_MESSAGE = 'Please try again or message support if the problem persists.';
export const getLoginFailureErrorMessage = () =>
  "Please try again. If you've forgotten your password, try resetting " +
  `your password through the 'Forgot my password' link below. Or contact ${getCompanyName()} support at: ${getCompanyPhone()}.`;
export const getAccountLockedLoginErrorMessage = () =>
  'This account has been locked because there have been too many failed login attempts. ' +
  `Please wait 1 hour and try again, or contact ${getCompanyName()} support at: ${getCompanyPhone()}.`;
export const getVerifyFailureErrorMessage = () =>
  `Unable to verify your info. Please try again or call us at ${getCompanyPhone()} if you need help.`;
export const NETWORK_FAILURE_ERROR_MESSAGE = 'Unable to connect. Please check your internet connection and retry.';
export const UNAUTHORIZED_ERROR_MESSAGE = 'Username or password is incorrect. Please try again.';
export const getUnderAgeEighteenErrorMessage = () =>
  `If you are under 18 years old, please have your parent or guardian call us at ${getCompanyPhone()}.`;

export const IOS_APP_STORE_LINK = 'https://apps.apple.com/us/app/alto-pharmacy/id1251765858';
export const IOS_APP_STORE_LINK_ITMS = 'itms-apps://itunes.apple.com/us/app/apple-store/id1251765858?mt=8';
export const ANDROID_APP_STORE_LINK = 'https://play.google.com/store/apps/details?id=com.scriptdash.altopharmacy';
export const ANDROID_APP_STORE_LINK_MARKET = 'market://details?id=com.scriptdash.altopharmacy';

export const CHECKOUT_ROUTE = 'checkout';

export const PREP_MEDICATION_GCNS = new Set(['57883', '76097', '76101', '76102', '75812']);

// alt texts for images in web/src/images and native/assets/images, keyed by file name
export const IMG_ALT_TEXTS = {
  addFamilyMember: 'family holding hands',
  addMedication: 'Illustration of a prescription with a transfer symbol',
  alertIssue: 'Illustration of a warning sign with an exclamation mark',
  alert: 'Illustration of an alert icon',
  assistantAvatar: 'assistant icon',
  autoRefill: 'Illustration of a pill bottle with a repeat icon',
  autoRefillEmpty: 'Illustration of a pill bottle with a repeat icon',
  autoRefillEnrolled: 'Illustration of a pill bottle with a repeat icon',
  autoRefillManagement: 'Illustration of a pill bottle with a repeat icon',
  bagWithMedications: 'Illustration of a bag with medications',
  bikeCourier: 'Illustration of a courier biking with a package',
  box: 'Illustration of an opened box',
  boxRotated: 'Box with meds',
  calendarAngled: 'Illustration of a calendar',
  cashPrice: 'Illustration of an open wallet with cash',
  careTeamAvatar: 'care team member icon',
  checklist: 'Illustration of a paper checklist',
  clock: 'Illustration of a clock',
  delivery: 'Illustration of hands holding a wrapped box',
  deliveryCourierWithPatient: 'Illustration of a courier delivering a package to a patient',
  deliveryWoman: 'Illustration of a delivery woman with a package',
  doublePillBottle: 'Illustration of two pill bottles',
  eIconInsuranceCircleNewYear: 'Illustration of a calendar',
  eIconInsuranceCircleOutOfDate: 'Illustration of health insurance card',
  eIconPaymentCircle: 'Illustration of a credit card',
  enterInsuranceManually: 'Illustration of form',
  fertilityPackageCheckmark: 'Illustration of a checkmark on a fertility package',
  unboxingGuide: 'Illustration of a checkmark on a fertility package',
  gears: 'Illustration of two interlocking gears',
  gift: 'Illustration of a wrapped box',
  greenCheckmark: 'Illustration of green checkmark',
  handheldMessaging: 'Illustration of a hand holding a smartphone that displays a text conversation',
  helpBubble: 'Illustration of chat bubble with question mark',
  helpUsImprove: 'Illustration of chat bubble with three dots',
  icePacks: 'Illustration of ice packs',
  infoRequestCompletion: 'Illustration of info request completion',
  insuranceCard: 'Illustration of health insurance card',
  insuranceEnterManual: 'Illustration of hand pointing up',
  insuranceNone: 'Illustration of circle with a slash through it',
  insuranceUpload: 'Illustration of upload icon',
  location: 'Illustration of a map pin',
  mail: 'Illustration of a mail envelope',
  logo: `${getCompanyName()} logo`,
  medication: 'Illustration of the medication',
  needleMeds: 'Illustration of a medication bottle and needle',
  notification: `Illustration of phone notification`,
  deliveredDelivery: 'Image of the delivered delivery',
  onboardingLanding: 'Illustration of a bag with medications',
  onboardingResume: 'Illustration of a paper checklist',
  onboardingWelcome: 'Illustration of mobile phone showing a pharmacist in a chat bubble and medications',
  orderBox: 'Illustration of an order in an open box',
  pharmacist: `Photographic headshot of Jenny, pharmacist`,
  pharmacyInfo: 'written prescription',
  pickup: 'Illustration of hands holding a wrapped box',
  phone: 'Illustration of a mobile phone',
  pillBottle: 'Illustration of a pill bottle with the lid off and pills falling in from the top',
  pillBottleViewfinder: 'Illustration of a pill bottle seen through a camera viewfinder',
  prescription: 'Illustration of a prescription',
  prescriptions: 'Illustration of inhaler, cream, and pill bottle',
  priceInvestigate: 'Illustration of a magnifying glass over a dollar sign on paper',
  progynyWelcome: 'Illustration of mobile phone showing a pharmacist, prescription, pill bottles, and video player',
  progynyAltoLogo: 'Progyny and Alto logo',
  rateShipment: 'Illustration of hands holding a wrapped box',
  receipts: 'Illustration of paper receipts',
  scheduleDelivery: 'Illustration of a calendar',
  secureMessaging: 'Illustration of a text conversation on a smartphone',
  serviceFee: 'Illustration of a person with package in hands',
  shield: 'Icon of a shield and lock',
  shipmentCourier: 'Illustration of a courier biking with a package',
  stethoscope: 'Illustration of a stethoscope.',
  syringe: 'Illustration of a syringe with vials',
  essentialIcon: 'Illustration of a pill bottle and medications',
  thumbsUp: 'Illustration of a thumbs up',
  transferMeds: 'Illustration of medications',
  transferPrescription: 'Illustration of a prescription with a transfer symbol',
  transferPrescriptions: 'paper airplane representing doctor prescription',
  topSellers: 'Illustration of top selling over the counter products',
  verifiedCheckmark: 'Illustration of a checkmark',
  wallet: 'Illustration of an open wallet with cash',
} as const;

export const phoneTypes = ['Mobile', 'Landline', 'VoIP'];

export const QUANTITY_UNIT_MAP = {
  EA: '',
  ML: 'mL',
  GM: 'g',
} as const;

export const COMMS_CHANNEL = {
  phone: 'Phone number',
  email: 'Email address',
} as const;

export const ORGANIC_ONBOARDING_STEPS = {
  HEALTH_PROFILE: 0,
  INSURANCE_FORM: 1,
  COMMUNICATION_PREFERENCES: 2,
} as const;

// These skus will receive free same-day delivery when they are dispensed from the NY facility.
export const NY_WAIVED_FEE_SKUS = [
  '00069108530',
  '00069034530',
  '00006505506',
  '00069110120',
  '00310744202',
  '00006505506',
];

// These following skus will receive free same-day delivery
export const AFREEZA_WAIVED_FEE_SKUS = [
  '47918087490',
  '47918087890',
  '47918088018',
  '47918089190',
  '47918089818',
  '47918090218',
];
export const BLUJEPA_WAIVED_FEE_SKU = '00173092245';
export const FUROSCIX_WAIVED_FEE_SKUS = ['71767010099', '71767010001', '71767010050', '71767010000', '71767011101'];
export const IBSRELA_WAIVED_FEE_SKU = '73154005060';
export const JOURNAVX_WAIVED_FEE_SKU = '51167054830';
export const NATROBA_WAIVED_FEE_SKU = '52246092904';
export const ORLYNVAH_WAIVED_FEE_SKU = '81153010001';
export const PIVYA_WAIVED_FEE_SKU = '62332096609';
export const SPINOSAD_WAIVED_FEE_SKUS = ['52246057004', '28595057004', '44183090004'];
export const TWIIST_WAIVED_FEE_SKUS = ['98617090400', '98617090100', '98617010100'];
export const VTAMA_WAIVED_FEE_SKU = '81672505101';
export const ZURZUVAE_WAIVED_FEE_SKUS = ['64406003002', '64406002901', '64406003001', '64406003101'];

export const SAME_DAY_DELIVERY_FEE_PILOT_FACILITY_IDS = {
  DALLAS: 10,
  NEW_YORK: 9,
  ORANGE_COUNTY: 1021,
} as const;
export const REHYDRATE = 'persist/REHYDRATE';

export const COMPLETE_ONBOARDING = 'COMPLETE_ONBOARDING';

export type SubscriptionType = keyof typeof subscriptionsConstants;
export const subscriptionsConstants = {
  general_updates: {
    category: `General ${getCompanyName()} updates and announcements`,
    description: `Information about ${getCompanyName()}'s services, products and new offerings`,
  },
  wellness_newsletters: {
    category: `${getCompanyName()}'s health and wellness newsletter`,
    description: 'Biweekly tips and health news from our pharmacists',
  },
  research_opportunities: {
    category: 'Research opportunities',
    description: 'Invitations to participate in paid user research studies',
  },
  nps_surveys: {
    category: 'Customer satisfaction surveys',
    description: "Periodic check-ins on how we're doing at delivering your medications",
  },
};

export type PaymentMethodType = 'PRE_TIP' | 'POST_TIP' | 'PRIMARY' | 'SECONDARY' | 'APPLE_PAY' | 'APPLE_PAY_W_TIP';

export const PAYMENT_METHOD_TYPES: Record<string, PaymentMethodType> = Object.freeze({
  PRE_TIP: 'PRE_TIP',
  POST_TIP: 'POST_TIP',
  PRIMARY: 'PRIMARY',
  SECONDARY: 'SECONDARY',
  APPLE_PAY: 'APPLE_PAY',
  APPLE_PAY_W_TIP: 'APPLE_PAY_W_TIP',
});

export type InsuranceSource =
  | 'banner'
  | 'med details'
  | 'onboarding'
  | 'profile'
  | 'add family member'
  | 'new_year_insurance_task';

export const USER_AUTHORIZED_KEY = '@user:authorized';

export const PROGYNY_BILLING_DISCLAIMER = {
  TITLE: '* Progyny Billing',
  BODY: 'You will receive an invoice from Progyny for your medications covered by Progyny Rx that will have the appropriate member responsibility applied from your medical carrier. Should Progyny later deny or revoke coverage, you may be held financially responsible for the full retail cost of the medications, subject to any applicable discounts or assistance programs.',
};

export const PASSWORD_ERROR_MESSAGE =
  'Must be at least 12 characters, with uppercase, lowercase, a number, and a special character.';

export const ALTO_COMPANY_ID = 1;
export const FUZE_HEALTH_COMPANY_ID = 34;
